/**
 * 用户仪表板 - 登录后的主界面
 */

import React, { useState } from 'react';
import { AppLayout } from './layout/AppLayout';
import { PromptsPage } from './pages/PromptsPage';
import { ImportPage } from './pages/ImportPage';
import { ProfilePage } from './pages/ProfilePage';
import { BackupSettingsPage } from './pages/BackupSettingsPage';
import { TrashPage } from './pages/TrashPage';
import { EmailVerificationNotice } from './EmailVerificationNotice';
import { RegistrationSuccessPage } from './pages/RegistrationSuccessPage';
import { useAuth } from '../contexts/AuthContext';

type PageType = 'dashboard' | 'prompts' | 'trash' | 'import' | 'profile' | 'backup' | 'create-prompt';

interface DashboardProps {
  defaultPage?: string;
}

export function Dashboard({ defaultPage = 'prompts' }: DashboardProps): JSX.Element {
  const { user, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState<PageType>(defaultPage as PageType);

  // 如果用户邮箱未验证，显示验证引导页面
  if (user && !user.emailVerified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <RegistrationSuccessPage 
          email={user.email} 
          onBackToLogin={() => {
            logout();
            window.location.href = '/';
          }}
        />
      </div>
    );
  }

  const handleNavigate = (page: string) => {
    setCurrentPage(page as PageType);
  };

  const renderPageContent = () => {
    switch (currentPage) {
      case 'prompts':
        return <PromptsPage onNavigate={handleNavigate} />;
      case 'trash':
        return <TrashPage onNavigate={handleNavigate} />;
      case 'create-prompt':
        return <PromptsPage autoCreate={true} onNavigate={handleNavigate} />;
      case 'import':
        return <ImportPage onNavigate={handleNavigate} />;
      case 'profile':
        return <ProfilePage onNavigate={handleNavigate} />;
      case 'backup':
        return <BackupSettingsPage onNavigate={handleNavigate} />;
      default:
        return <DashboardHome onNavigate={handleNavigate} />;
    }
  };

  return (
    <AppLayout currentPage={currentPage} onNavigate={handleNavigate}>
      {renderPageContent()}
    </AppLayout>
  );
}

// 仪表板首页组件
function DashboardHome({ onNavigate }: { onNavigate: (page: string) => void }): JSX.Element {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // 加载统计数据
  React.useEffect(() => {
    const loadStats = async () => {
      try {
        const { PromptService } = await import('../services/prompts');
        const response = await PromptService.getStats();
        setStats(response.stats);
      } catch (error) {
        console.error('加载统计数据失败:', error);
        // 使用默认统计数据
        setStats({
          totalPrompts: 0,
          totalCategories: 0,
          totalTags: 0,
          recentPrompts: 0,
          favoritePrompts: 0,
        });
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, []);

  const quickActions = [
    {
      title: '创建提示词',
      description: '开始创建您的第一个提示词',
      icon: '✏️',
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      action: () => onNavigate('create-prompt'),
    },
    {
      title: '批量导入',
      description: '从JSON文件导入现有提示词',
      icon: '📥',
      color: 'bg-green-50 border-green-200 text-green-700',
      action: () => onNavigate('import'),
    },
    {
      title: '浏览提示词库',
      description: '查看和管理您的所有提示词',
      icon: '📚',
      color: 'bg-purple-50 border-purple-200 text-purple-700',
      action: () => onNavigate('prompts'),
    },
  ];

  const displayStats = statsLoading ? [
    {
      label: '提示词总数',
      value: '...',
      icon: '📝',
      color: 'text-blue-600',
    },
    {
      label: '分类数量',
      value: '...',
      icon: '📁',
      color: 'text-green-600',
    },
    {
      label: '收藏提示词',
      value: '...',
      icon: '⭐',
      color: 'text-purple-600',
    },
  ] : [
    {
      label: '提示词总数',
      value: stats?.totalPrompts || 0,
      icon: '📝',
      color: 'text-blue-600',
    },
    {
      label: '分类数量',
      value: stats?.totalCategories || 0,
      icon: '📁',
      color: 'text-green-600',
    },
    {
      label: '收藏提示词',
      value: stats?.favoritePrompts || 0,
      icon: '⭐',
      color: 'text-purple-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* 欢迎区域 */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          欢迎回来！{user?.email.split('@')[0]}
        </h1>
        <p className="text-blue-100">
          开始管理您的提示词库，提升AI工作效率
        </p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {displayStats.map((stat: any, index: number) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <span className="text-2xl mr-3">{stat.icon}</span>
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 快速操作 */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.action}
              className={`p-6 rounded-lg border-2 border-dashed transition-all hover:border-solid hover:shadow-md ${action.color}`}
            >
              <div className="text-center">
                <div className="text-3xl mb-3">{action.icon}</div>
                <h3 className="font-medium mb-2">{action.title}</h3>
                <p className="text-sm opacity-90">{action.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 最近活动 */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">最近活动</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            <span className="text-sm text-gray-600">
              账户创建于 {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('zh-CN') : '未知时间'}
            </span>
          </div>
          <div className="text-center py-8 text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="mt-2 text-sm">暂无更多活动记录</p>
          </div>
        </div>
      </div>
    </div>
  );
} 